import type {
  ToolCallMessagePartComponent,
  ToolCallMessagePartStatus,
} from '@assistant-ui/react';
import {type AutoColor, Icon, type IconName} from '@wandb/components';
import React, {memo, useContext, useMemo, useState} from 'react';
import {DisclosureStateContext} from 'react-aria-components';

import {CodeSnippet} from '../CodeSnippet';
import {ErrorBoundary} from '../ErrorBoundary';
import {AgentActionButton} from './AgentActionButton';
import {
  ASK_USER_TOOL_NAME,
  REQUEST_NETWORK_ACCESS_TOOL_NAME,
} from './constants';
import {isAgentAction} from './knownActions';
import {extractToolArgsPreview} from './previews';
import {useTrackWBAgentInspection} from './useTrackWBAgent';
import {extractPythonCodeBlock, isShellResult} from './utils';
import {WBAgentStepDisclosure} from './WBAgentStepDisclosure';

type NetworkAccessArgs = {reason?: string; domains?: string[]};

const parseNetworkAccessArgs = (argsText?: string): NetworkAccessArgs => {
  if (!argsText) {
    return {};
  }
  try {
    const parsed = JSON.parse(argsText);
    return typeof parsed === 'object' && parsed != null ? parsed : {};
  } catch {
    return {};
  }
};

type AskUserQuestion = {
  question?: string;
  options?: string[];
};

const parseAskUserQuestions = (argsText?: string): AskUserQuestion[] => {
  if (!argsText) {
    return [];
  }
  try {
    const parsed = JSON.parse(argsText);
    return Array.isArray(parsed?.questions) ? parsed.questions : [];
  } catch {
    // not valid JSON — render the indicator without an expandable list
    return [];
  }
};

/**
 * AskUserIndicator renders a completed ask_user call as a small indicator that
 * the agent posed question(s) — not an error-styled tool row. It expands to
 * show the questions and their options.
 */
const AskUserIndicator: React.FC<{argsText?: string}> = ({argsText}) => {
  const [expanded, setExpanded] = useState(false);
  const questions = useMemo(() => parseAskUserQuestions(argsText), [argsText]);
  const count = questions.length;
  const label = count > 1 ? `Asked ${count} questions` : 'Asked a question';
  const canExpand = count > 0;

  return (
    <div className="flex flex-col gap-8 py-4">
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center gap-8 -mx-6 px-6 py-4 text-left transition-colors rounded-md bg-transparent hover:bg-moon-100 cursor-pointer text-moon-800 disabled:cursor-default disabled:hover:bg-transparent">
        <span className="inline-flex shrink-0">
          <Icon icon="forum-chat-bubble" size="sm" color="moon" />
        </span>
        <span className="text-left leading-[21px] font-semibold text-sm">
          {label}
        </span>
        <span className="flex-1" />
        {canExpand && (
          <span
            className={`inline-flex shrink-0 text-moon-450 transition-[rotate] duration-[220ms] ${
              expanded ? 'rotate-90' : 'rotate-0'
            }`}>
            <Icon icon="chevron-next" size="sm" />
          </span>
        )}
      </button>
      {expanded && (
        <div className="flex flex-col gap-8 border-l-2 border-moon-200 pl-12">
          {questions.map((q, i) => (
            // oxlint-disable-next-line react/no-array-index-key
            <div key={i} className="flex flex-col gap-4">
              <span className="text-sm text-moon-700">{q.question}</span>
              {Array.isArray(q.options) && q.options.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {q.options.map(option => (
                    <span
                      key={option}
                      className="rounded bg-moon-100 px-6 py-1 text-xs text-moon-600">
                      {option}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * NetworkAccessIndicator renders a completed request_network_access call as a
 * small indicator that the agent asked for network access — not an error-styled
 * tool row. It expands to show the reason and the requested domains.
 */
const NetworkAccessIndicator: React.FC<{argsText?: string}> = ({argsText}) => {
  const [expanded, setExpanded] = useState(false);
  const {reason, domains} = useMemo(
    () => parseNetworkAccessArgs(argsText),
    [argsText]
  );
  const domainList = Array.isArray(domains) ? domains : [];
  const summary =
    domainList.length > 0
      ? `Requested network access to ${domainList.join(', ')}`
      : 'Requested network access';
  const canExpand = !!reason || domainList.length > 0;

  return (
    <div className="flex flex-col gap-8 py-4">
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center gap-8 -mx-6 px-6 py-4 text-left transition-colors rounded-md bg-transparent hover:bg-moon-100 cursor-pointer text-moon-800 disabled:cursor-default disabled:hover:bg-transparent">
        <span className="inline-flex shrink-0">
          <Icon icon="lock-open" size="sm" color="sienna" />
        </span>
        <span className="text-left leading-[21px] font-semibold text-sm">
          {summary}
        </span>
        <span className="flex-1" />
        {canExpand && (
          <span
            className={`inline-flex shrink-0 text-moon-450 transition-[rotate] duration-[220ms] ${
              expanded ? 'rotate-90' : 'rotate-0'
            }`}>
            <Icon icon="chevron-next" size="sm" />
          </span>
        )}
      </button>
      {expanded && (
        <div className="flex flex-col gap-8 border-l-2 border-moon-200 pl-12">
          {reason && <span className="text-sm text-moon-700">{reason}</span>}
          {domainList.length > 0 && (
            <div className="flex flex-wrap gap-4">
              {domainList.map(domain => (
                <span
                  key={domain}
                  className="rounded bg-moon-100 px-6 py-1 text-xs font-[Inconsolata] text-moon-600">
                  {domain}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const statusConfig: Record<
  ToolCallMessagePartStatus['type'] | 'error',
  {icon: IconName; color: AutoColor}
> = {
  running: {icon: 'loading', color: 'moon'},
  complete: {icon: 'checkmark2', color: 'green'},
  incomplete: {icon: 'failed-error-deleted', color: 'red'},
  error: {icon: 'failed-error-deleted', color: 'red'},
  'requires-action': {icon: 'warning', color: 'sienna'},
};

const ToolCallCodeSnippet = ({
  codeSnippet,
  language = 'json',
  title,
}: {
  codeSnippet: string;
  language?: string;
  title?: string;
}) => {
  const {trackCopied} = useTrackWBAgentInspection();
  let formattedCode;
  try {
    formattedCode = JSON.stringify(JSON.parse(codeSnippet), null, 2);
  } catch {
    formattedCode = codeSnippet;
  }
  return (
    <div>
      {title && (
        <div className="py-4 font-normal text-sm font-[Inconsolata] text-moon-500">
          {title}
        </div>
      )}
      <CodeSnippet
        showExpandCollapse={true}
        codeSnippet={formattedCode}
        codeLanguage={language}
        fontFamily="Inconsolata"
        showBorder={false}
        analyticsEvent={() => trackCopied('tool_call')}
      />
    </div>
  );
};

const ToolCallArgs: React.FC<{argsText?: string}> = ({argsText}) => {
  if (!argsText) {
    return null;
  }
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(argsText);
  } catch {
    // not valid JSON — fall through to generic display
  }

  const command =
    parsed && typeof parsed.command === 'string' ? parsed.command : null;
  const pythonCodeBlock = command ? extractPythonCodeBlock(command) : null;

  const diff = parsed && typeof parsed.diff === 'string' ? parsed.diff : null;
  const diffLang =
    parsed?.path && String(parsed.path).endsWith('.py') ? 'python' : 'text';

  const snippet = pythonCodeBlock
    ? {code: pythonCodeBlock, language: 'python', title: 'python'}
    : command
      ? {code: command, language: 'bash', title: 'bash'}
      : diff
        ? {
            code: diff,
            language: diffLang,
            title: `${parsed?.operation ?? 'patch'}: ${parsed?.path ?? ''}`,
          }
        : {code: argsText, language: 'json', title: undefined};

  return (
    <div>
      <p className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
        ARGS
      </p>
      <ToolCallCodeSnippet
        codeSnippet={snippet.code}
        language={snippet.language}
        title={snippet.title}
      />
      {parsed?.timeout_ms != null && (
        <div className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
          Timeout: {String(parsed.timeout_ms)}
        </div>
      )}
    </div>
  );
};

const ToolCallResult: React.FC<{result?: unknown}> = ({result}) => {
  const resultStr = typeof result === 'string' ? result : undefined;
  if (resultStr === undefined) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(resultStr);
  } catch {
    parsed = null;
  }

  let resultBody: React.JSX.Element;
  if (isShellResult(parsed)) {
    const {stdout = '', stderr = '', exit_code} = parsed;
    resultBody = (
      <div className="gap-4">
        {stdout && (
          <ToolCallCodeSnippet codeSnippet={stdout} title={'stdout'} />
        )}
        {stderr && (
          <ToolCallCodeSnippet codeSnippet={stderr} title={'stderr'} />
        )}
        {exit_code !== undefined && (
          <div className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
            Exit code: {exit_code}
          </div>
        )}
      </div>
    );
  } else {
    resultBody = <ToolCallCodeSnippet codeSnippet={resultStr} />;
  }
  return (
    <div className="border-t border-dashed border-moon-250 pt-8">
      <p className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
        OUTPUT
      </p>
      {resultBody}
    </div>
  );
};

const ToolCallError: React.FC<{
  status: ToolCallMessagePartStatus;
}> = ({status}) => {
  if (status.type !== 'incomplete') {
    return null;
  }
  const error = status.error;
  const errorText = error
    ? typeof error === 'string'
      ? error
      : JSON.stringify(error)
    : null;
  if (!errorText) {
    return null;
  }
  return (
    <div className="px-12">
      <p className="font-semibold text-xs text-moon-500">
        {status.reason === 'cancelled' ? 'Cancelled reason:' : 'Error:'}
      </p>
      <p className="text-moon-500">{errorText}</p>
    </div>
  );
};

const LazyDisclosureContent: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const state = useContext(DisclosureStateContext);
  if (!state?.isExpanded) {
    return null;
  }
  return (
    <div className="relative flex flex-col gap-8 border-moon-250">
      {children}
    </div>
  );
};

const ToolCallImpl: ToolCallMessagePartComponent = props => (
  <ErrorBoundary
    silent
    onError={error => {
      console.error('[WBAgentToolCall] render crash', error);
    }}
    renderError={() => (
      <div className="rounded-lg border border-red-300 bg-red-50 p-8 text-sm text-red-700">
        <div className="font-semibold">Tool call failed to render</div>
        <div className="mt-4 text-xs">
          Something went wrong rendering this tool call. The error has been
          logged.
        </div>
      </div>
    )}>
    <ToolCallImplInner {...props} />
  </ErrorBoundary>
);

const ToolCallImplInner: ToolCallMessagePartComponent = ({
  toolName,
  argsText,
  result,
  status,
  isError,
}: {
  toolName: string;
  argsText?: string;
  result?: unknown;
  status: ToolCallMessagePartStatus;
  isError?: boolean;
}) => {
  if (toolName === ASK_USER_TOOL_NAME) {
    return <AskUserIndicator argsText={argsText} />;
  }

  if (toolName === REQUEST_NETWORK_ACCESS_TOOL_NAME) {
    return <NetworkAccessIndicator argsText={argsText} />;
  }

  // UI tools (agent actions) render as their button — enabled, or disabled with
  // a hint when not actionable here. Everything else (including calls to unknown
  // tools) falls through to the standard tool-call rendering below.
  if (isAgentAction(toolName)) {
    return <AgentActionButton name={toolName} argsText={argsText} />;
  }

  const isCancelled =
    status.type === 'incomplete' && status.reason === 'cancelled';
  const isRunning = status.type === 'running';
  const statusType = isError ? 'error' : status.type;
  const config = statusConfig[statusType];
  return (
    <div className={isCancelled ? 'opacity-60' : undefined}>
      <WBAgentStepDisclosure
        icon={config.icon}
        iconColor={config.color}
        iconSpin={isRunning}
        isRunning={isRunning}
        label={toolName}
        stepKind="tool_call"
        toolName={toolName}
        preview={extractToolArgsPreview(argsText)}>
        <LazyDisclosureContent>
          <div className="rounded-md border border-moon-250 p-12 dark:border-moon-700">
            <ToolCallError status={status} />
            <ToolCallArgs argsText={argsText} />
            {!isCancelled && <ToolCallResult result={result} />}
          </div>
        </LazyDisclosureContent>
      </WBAgentStepDisclosure>
    </div>
  );
};

export const WBAgentToolCall = memo(
  ToolCallImpl
) as ToolCallMessagePartComponent;
WBAgentToolCall.displayName = 'WBAgentToolCall';
